import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthProvider.js';
import { listenForegroundPush, resumePushIfEnabled } from '../lib/messaging.js';
import { SignInScreen } from '../features/auth/SignInScreen.js';
import { DomainRejectedScreen } from '../features/auth/DomainRejectedScreen.js';
import { OrgProvider, useOrgs } from '../features/orgs/OrgProvider.js';
import { CreateOrgScreen } from '../features/orgs/CreateOrgScreen.js';
import { OrgSettingsPage } from '../features/orgs/OrgSettingsPage.js';
import { AppShell } from './AppShell.js';
import { DashboardPage } from '../features/dashboard/DashboardPage.js';
import { ProjectsListPage } from '../features/projects/ProjectsListPage.js';
import { ProjectLayout } from '../features/projects/ProjectLayout.js';
import { ProjectProvider } from '../features/projects/ProjectProvider.js';
import { ProjectOverviewPage } from '../features/projects/ProjectOverviewPage.js';
import { ProjectSettingsPage } from '../features/projects/ProjectSettingsPage.js';
import { PrdPage } from '../features/prd/PrdPage.js';
import { TddPage } from '../features/tdd/TddPage.js';
import { TasksHubPage } from '../features/tasks/TasksHubPage.js';
import { TaskDetailPage } from '../features/tasks/TaskDetailPage.js';
import { DiscussionsPage } from '../features/discussions/DiscussionsPage.js';
import { MyTasksPage } from '../features/tasks/MyTasksPage.js';
import { DeliverablesPage } from '../features/deliverables/DeliverablesPage.js';
import { ProjectActivityPage } from '../features/activity/ProjectActivityPage.js';
import { VersionHistoryPage } from '../features/documents/VersionHistoryPage.js';
import { RegenerationPage } from '../features/regeneration/RegenerationPage.js';
import { TimelineLandingPage } from '../features/timeline/TimelineLandingPage.js';
import { SettingsPage } from '../features/settings/SettingsPage.js';
import { PrintPage } from '../features/exports/PrintPage.js';
import { AuthorizeAppPage } from '../features/mcp/AuthorizeAppPage.js';
import { TrainingSourcesPage } from '../features/training/TrainingSourcesPage.js';
import { TrainingSessionsPage } from '../features/training/TrainingSessionsPage.js';
import { TrainingSessionPage } from '../features/training/TrainingSessionPage.js';
import { SlidesEditorPage } from '../features/training/SlidesEditorPage.js';

function FullScreenSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg text-muted">
      <span className="text-sm">Loading…</span>
    </div>
  );
}

function SignedInApp() {
  const { loading, orgs } = useOrgs();

  // Resumes push silently (refreshing a rotated token) only for a device that already opted in
  // — never prompts. The foreground listener shows the same notification the service worker
  // would when the tab is backgrounded, so behavior is consistent either way. See lib/messaging.
  useEffect(() => {
    void resumePushIfEnabled();
    let unsubscribe: (() => void) | undefined;
    void listenForegroundPush().then((unsub) => {
      unsubscribe = unsub;
    });
    return () => unsubscribe?.();
  }, []);

  if (loading) return <FullScreenSpinner />;
  if (orgs.length === 0) return <CreateOrgScreen />;

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/projects/:projectId/print/:docType"
          element={
            <ProjectProvider>
              <PrintPage />
            </ProjectProvider>
          }
        />
        <Route element={<AppShell />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/projects" element={<ProjectsListPage />} />
          <Route path="/my-tasks" element={<MyTasksPage />} />
          <Route path="/timeline" element={<TimelineLandingPage />} />
          {/* Team activity is disabled for now: redirect old links/bookmarks to the dashboard.
              To re-enable, render <TeamActivityPage /> here and restore its nav item in AppShell. */}
          <Route path="/team-activity" element={<Navigate to="/" replace />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/org-settings" element={<OrgSettingsPage />} />

          <Route path="/projects/:projectId" element={<ProjectLayout />}>
            <Route index element={<ProjectOverviewPage />} />
            <Route path="prd" element={<PrdPage />} />
            <Route path="tdd" element={<TddPage />} />
            <Route path="tasks" element={<TasksHubPage />} />
            <Route path="tasks/:taskId" element={<TaskDetailPage />} />
            <Route path="board" element={<Navigate to="../tasks?view=board" replace />} />
            <Route path="timeline" element={<Navigate to="../tasks?view=timeline" replace />} />
            <Route path="discussions" element={<DiscussionsPage />} />
            <Route path="deliverables" element={<DeliverablesPage />} />
            <Route path="activity" element={<ProjectActivityPage />} />
            <Route path="documents/:docType/history" element={<VersionHistoryPage />} />
            <Route path="regenerations" element={<RegenerationPage />} />
            <Route path="settings" element={<ProjectSettingsPage />} />
            <Route path="sources" element={<TrainingSourcesPage />} />
            <Route path="sessions" element={<TrainingSessionsPage />} />
            <Route path="sessions/:sessionId" element={<TrainingSessionPage />} />
            <Route path="sessions/:sessionId/edit" element={<SlidesEditorPage />} />
          </Route>
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export function App() {
  const { status } = useAuth();

  if (status === 'loading') return <FullScreenSpinner />;
  if (status === 'signed-out') return <SignInScreen />;
  if (status === 'domain-rejected') return <DomainRejectedScreen />;

  // The MCP consent screen is deliberately outside OrgProvider and the app shell. A user arrives
  // here mid-OAuth-redirect from their terminal, with one decision to make and nothing else to
  // do; routing it through SignedInApp would put it behind the "create your first org" gate and
  // strand anyone who has not set one up yet. Sign-in still applies — the page cannot render
  // until `status` is signed-in — which is exactly the authentication the flow depends on.
  if (window.location.pathname === '/authorize-app') return <AuthorizeAppPage />;

  return (
    <OrgProvider>
      <SignedInApp />
    </OrgProvider>
  );
}
